import React, { useState } from 'react';
import { Play, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

interface LazyVideoPlayerProps {
  videoUrl: string;
  provider: 'youtube' | 'vimeo' | string | null;
  coverImage: string;
  title: string;
}

const LazyVideoPlayer: React.FC<LazyVideoPlayerProps> = ({
  videoUrl,
  provider,
  coverImage,
  title
}) => {
  const { language } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Convert watch/share URLs to clean privacy-enhanced embed URLs
  const embedUrl = React.useMemo(() => {
    if (!videoUrl) return '';
    
    if (provider === 'youtube') {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = videoUrl.match(regExp);
      if (match && match[2].length === 11) {
        return `https://www.youtube-nocookie.com/embed/${match[2]}?autoplay=1&rel=0&modestbranding=1`;
      }
    } else if (provider === 'vimeo') {
      const regExp = /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
      const match = videoUrl.match(regExp);
      if (match) {
        return `https://player.vimeo.com/video/${match[1]}?autoplay=1&title=0&byline=0&portrait=0`;
      }
    }
    return videoUrl;
  }, [videoUrl, provider]);

  if (hasError) {
    return (
      <div className="relative aspect-video rounded-2xl overflow-hidden bg-card border border-border/20 flex flex-col items-center justify-center p-6 text-center text-muted-foreground">
        <AlertCircle className="w-10 h-10 text-red-500/80 mb-3" />
        <p className="text-sm font-semibold text-white">
          {language === 'TR' ? "Video Yüklenemedi" : "Video Failed to Load"}
        </p>
        <p className="text-xs max-w-xs mt-1">
          {language === 'TR' ? "Bu video şu anda erişilebilir değil veya kaldırılmış olabilir." : "This video is currently unavailable or may have been removed."}
        </p>
        <a 
          href={videoUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-xs text-white underline mt-4 hover:text-muted-foreground transition-colors"
        >
          {language === 'TR' ? "Doğrudan Kaynağında İzleyin" : "Watch on Original Platform"}
        </a>
      </div>
    );
  }

  if (isPlaying && embedUrl) {
    return (
      <div className="relative aspect-video rounded-2xl overflow-hidden bg-card border border-border/20 shadow-2xl">
        <iframe
          src={embedUrl}
          title={language === 'TR' ? `${title} Video Oynatıcı` : `${title} Video Player`}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          onError={() => setHasError(true)}
          className="absolute inset-0 w-full h-full"
        ></iframe>
      </div>
    );
  }

  const playAriaLabel = language === 'TR' ? `${title} videosunu oynat` : `Play video: ${title}`;
  const imageAlt = language === 'TR' ? `${title} Video Kapak Görseli` : `${title} Video Cover Visual`;

  return (
    <div 
      className="relative aspect-video rounded-2xl overflow-hidden bg-card border border-border/20 shadow-2xl cursor-pointer group"
      onClick={() => setIsPlaying(true)}
      aria-label={playAriaLabel}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          setIsPlaying(true);
        }
      }}
    >
      <img
        src={coverImage}
        alt={imageAlt}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02] filter brightness-[0.7]"
        loading="lazy"
      />
      {/* Dynamic shadow gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-background/30 via-transparent to-transparent" />
      
      {/* Play Overlay Button */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center group-hover:scale-110 group-hover:bg-white group-hover:border-white transition-all duration-300 shadow-xl">
          <Play className="w-6 h-6 text-white group-hover:text-black transition-colors fill-current ml-1" />
        </div>
      </div>
    </div>
  );
};

export default LazyVideoPlayer;
